import React from 'react';
import { useTranslation } from 'react-i18next';
import { LineChart as LineChartIcon, RefreshCw } from 'lucide-react';
import { XAxis, YAxis, Tooltip, ResponsiveContainer, Area, Bar, ComposedChart } from 'recharts';

export function IntangiHubChart({ chartData, symbol, loading }) {
  const { t } = useTranslation();
  if (loading) {
    return (
      <div className="bg-[#090B0F] border border-white/10 p-4" data-testid="intangihub-chart">
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="w-5 h-5 text-purple-400 animate-spin" />
        </div>
      </div>
    );
  }

  const candles = chartData?.data || [];

  if (candles.length === 0) {
    return (
      <div className="bg-[#090B0F] border border-white/10 p-4" data-testid="intangihub-chart">
        <div className="flex items-center gap-2 mb-4">
          <LineChartIcon className="w-5 h-5 text-purple-400" />
          <h3 className="text-sm uppercase tracking-[0.2em] font-semibold text-white">{symbol || 'IP Asset'} Chart</h3>
        </div>
        <div className="text-center py-8">
          <LineChartIcon className="w-8 h-8 text-slate-600 mx-auto mb-2" />
          <p className="text-slate-500 text-xs font-mono">{t('dashboard.waitingData')}</p>
        </div>
      </div>
    );
  }

  const formatted = candles.map((c, i) => ({
    idx: i,
    time: c.timestamp ? new Date(c.timestamp).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' }) : `${i}`,
    open: c.open, high: c.high, low: c.low, close: c.close, volume: c.volume || 0,
  }));

  const lastPrice = formatted[formatted.length - 1]?.close || 0;
  const firstPrice = formatted[0]?.open || lastPrice;
  const priceChange = lastPrice - firstPrice;
  const pctChange = firstPrice > 0 ? ((priceChange / firstPrice) * 100) : 0;

  return (
    <div className="bg-[#090B0F] border border-white/10 p-4" data-testid="intangihub-chart">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <LineChartIcon className="w-5 h-5 text-purple-400" />
          <h3 className="text-sm uppercase tracking-[0.2em] font-semibold text-white">{symbol} OHLCV</h3>
        </div>
        <div className="flex items-center gap-2">
          <span className="font-mono text-white text-sm">${lastPrice.toFixed(4)}</span>
          <span className={`font-mono text-xs ${priceChange >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
            {priceChange >= 0 ? '+' : ''}{pctChange.toFixed(2)}%
          </span>
        </div>
      </div>
      <div className="h-[200px]">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={formatted}>
            <defs>
              <linearGradient id="colorIpClose" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#A855F7" stopOpacity={0.3} />
                <stop offset="95%" stopColor="#A855F7" stopOpacity={0} />
              </linearGradient>
            </defs>
            <XAxis dataKey="time" axisLine={false} tickLine={false} tick={{ fill: '#64748B', fontSize: 9 }} interval="preserveStartEnd" />
            <YAxis axisLine={false} tickLine={false} tick={{ fill: '#64748B', fontSize: 9 }} domain={['auto', 'auto']}
              tickFormatter={(v) => v > 1000 ? `$${(v / 1000).toFixed(1)}K` : `$${v.toFixed(2)}`} />
            <Tooltip
              contentStyle={{ backgroundColor: '#11141D', border: '1px solid rgba(168,85,247,0.3)', borderRadius: 0, fontSize: 11 }}
              labelStyle={{ color: '#94A3B8' }}
              formatter={(v, name) => [`$${Number(v).toFixed(4)}`, name.charAt(0).toUpperCase() + name.slice(1)]} />
            <Bar dataKey="volume" fill="#A855F720" yAxisId={0} />
            <Area type="monotone" dataKey="close" stroke="#A855F7" strokeWidth={2} fillOpacity={1} fill="url(#colorIpClose)" />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
